import { getCurrentTenant, type TenantId } from '@utils/tenant-utils'
import { ApiClient, ApiError } from './ApiClient'

export type ContractorDocumentStatus = 'Active' | 'Inactive'

export interface ContractorDocumentModel {
  id: string
  tenantId: string
  contractorId: string
  documentId: string
  fileName: string | null
  documentType: string
  contentType: string | null
  description: string | null
  status: ContractorDocumentStatus
  size: number | null
  createdAt: string
  uploadedAt: string | null
  uploadedBy: string | null
  modifiedAt: string | null
}

export function getContractorDocumentFilename(document?: ContractorDocumentModel | null): string {
  if (!document) return ''
  if (typeof document.fileName === 'string' && document.fileName.trim().length > 0) {
    return document.fileName.trim()
  }
  return ''
}

export interface GetContractorDocumentsParams {
  ownerId: string
  tenantId?: TenantId
  pageSize: number
  continuationToken?: string
  sort?: 'createdAt_desc' | 'createdAt_asc' | 'uploadedAt_desc' | 'uploadedAt_asc'
  dateFrom?: string
  dateTo?: string
  documentType?: string
  status?: string
}

export interface GetContractorDocumentsResponse {
  documents: ContractorDocumentModel[]
  continuationToken: string
  hasMoreResults: boolean
}

export interface PostContractorDocumentBody {
  documentType: string
  description: string | null
  status: ContractorDocumentStatus
  file: File
}

export interface PutContractorDocumentBody {
  documentType: string
  description: string | null
  status: ContractorDocumentStatus
}

export async function getContractorDocuments(
  params: GetContractorDocumentsParams,
): Promise<GetContractorDocumentsResponse> {
  const queryParams: Record<string, string> = {}
  if (params.sort) queryParams.sort = params.sort
  if (params.dateFrom) queryParams.dateFrom = params.dateFrom
  if (params.dateTo) queryParams.dateTo = params.dateTo
  if (params.documentType) queryParams.documentType = params.documentType
  if (params.status) queryParams.status = JSON.stringify(params.status)

  const headers: Record<string, string> = {
    'x-tenant-id': params.tenantId ?? getCurrentTenant(),
    'x-page-size': params.pageSize.toString(),
  }
  if (params.continuationToken) {
    headers['x-continuation-token'] = params.continuationToken
  }

  const response = await ApiClient.get<ContractorDocumentModel[]>(
    `/contractors/${params.ownerId}/documents`,
    {
      headers,
      params: queryParams,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const continuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    documents: response.data,
    continuationToken: continuationToken ?? '',
    hasMoreResults,
  }
}

export async function createContractorDocument(
  contractorId: string,
  body: PostContractorDocumentBody,
): Promise<ContractorDocumentModel> {
  const tenantId = getCurrentTenant()
  const formData = new FormData()
  formData.append('DocumentType', body.documentType)
  if (body.description) formData.append('Description', body.description)
  formData.append('Status', body.status)
  formData.append('File', body.file)

  const response = await ApiClient.post<ContractorDocumentModel>(
    `/contractors/${contractorId}/documents`,
    formData,
    {
      headers: { 'x-tenant-id': tenantId },
    },
  )
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  return response.data
}

export async function updateContractorDocument(
  id: string,
  contractorId: string,
  body: PutContractorDocumentBody,
): Promise<ContractorDocumentModel> {
  const tenantId = getCurrentTenant()
  const response = await ApiClient.put<ContractorDocumentModel>(
    `/contractors/${contractorId}/documents/${id}`,
    body,
    {
      headers: { 'x-tenant-id': tenantId },
    },
  )
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  return response.data
}

export async function uploadContractorDocumentContent(
  documentId: string,
  contractorId: string,
  file: File,
): Promise<ContractorDocumentModel> {
  const tenantId = getCurrentTenant()
  const formData = new FormData()
  formData.append('File', file)

  const response = await ApiClient.put<ContractorDocumentModel>(
    `/contractors/${contractorId}/documents/${documentId}/content`,
    formData,
    {
      headers: { 'x-tenant-id': tenantId },
    },
  )
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  return response.data
}

export async function deleteContractorDocument(
  contractorId: string,
  documentId: string,
): Promise<void> {
  const tenantId = getCurrentTenant()
  const response = await ApiClient.delete<void>(
    `/contractors/${contractorId}/documents/${documentId}`,
    {
      headers: { 'x-tenant-id': tenantId },
    },
  )
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
}

export async function downloadContractorDocumentContent(
  documentId: string,
  contractorId: string,
  filename: string,
): Promise<void> {
  const tenantId = getCurrentTenant()
  const response = await ApiClient.get<Blob>(
    `/contractors/${contractorId}/documents/${documentId}/content`,
    {
      headers: { 'x-tenant-id': tenantId },
      responseType: 'blob',
    },
  )
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  const url = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename || `document-${documentId}`
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
